import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';

const API_URL = 'https://onlineico.com/api';

// Style anti-autofill navigateur (fond blanc)
const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', color: '#F1F5F9',
    fontSize: '0.88rem', fontFamily: 'Inter, sans-serif',
    outline: 'none', boxSizing: 'border-box',
    WebkitBoxShadow: '0 0 0 1000px rgba(11,17,32,0.95) inset',
    WebkitTextFillColor: '#F1F5F9',
};

const SERVICES = [
  {
    emoji: '🪙',
    title: 'Token Creation',
    desc: 'We create your custom ERC-20 / BEP-20 token: name, ticker, total supply, tokenomics, taxes. Ready to deploy.',
    color: '#34D399',
    border: 'rgba(52,211,153,0.2)',
    bg: 'rgba(52,211,153,0.06)',
    items: ['ERC-20 / BEP-20 token', 'Custom tokenomics', 'Configurable buy/sell taxes', 'Contract verification'],
  },
  {
    emoji: '📄',
    title: 'Smart Contract',
    desc: 'Development of audited and secure smart contracts for your presale, vesting, staking or launchpad.',
    color: '#8B5CF6',
    border: 'rgba(139,92,246,0.2)',
    bg: 'rgba(139,92,246,0.06)',
    items: ['Presale contract', 'Vesting & lockup', 'Staking rewards', 'Anti-bot & security'],
  },
  {
    emoji: '🔒',
    title: 'KYC & Audit',
    desc: 'Get your smart contract audited and obtain your KYC badge to build investor trust.',
    color: '#3B82F6',
    border: 'rgba(59,130,246,0.2)',
    bg: 'rgba(59,130,246,0.06)',
    items: ['Smart contract audit', 'Security report', 'Verified KYC badge', 'Report publication'],
  },
];

const PROCESS = [
  { step: '01', title: 'Contact', desc: 'Fill in the form or contact us directly. We analyse your needs.', color: '#34D399' },
  { step: '02', title: 'Quote', desc: 'We send you a personalised quote within 24h.', color: '#8B5CF6' },
  { step: '03', title: 'Development', desc: 'Our team develops and tests your solution.', color: '#F59E0B' },
  { step: '04', title: 'Delivery', desc: 'Deployment, verification and ownership transfer.', color: '#3B82F6' },
];

export default function TokenStudio() {
  const navigate = useNavigate();

  const [form, setForm]       = useState({ name: '', email: '', service: '', description: '' });
  const [status, setStatus]   = useState(null); // null | 'sending' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
        setErrorMsg('Please fill in all required fields.');
        setStatus('error');
        return;
    }
    setStatus('sending');
    try {
        const message = `Service requested: ${form.service}\n\n${form.description || 'No description provided.'}`;
        const res = await fetch(`${API_URL}/contact.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: form.name, email: form.email, message }),
        });
        const data = await res.json();
        if (data.success) {
            setStatus('success');
            setForm({ name: '', email: '', service: '', description: '' });
        } else {
            setErrorMsg(data.error || 'An error occurred. Please try again.');
            setStatus('error');
        }
    } catch {
        setErrorMsg('Network error. Please try again.');
        setStatus('error');
    }
  };

  return (
    <div className="app-wrap">
<Helmet>
    <title>Token Studio | Token Creation, Smart Contracts & ICO Launch | OnlineICO</title>

    <meta name="description" content="Professional crypto services: ERC-20 / BEP-20 token creation, smart contract development, KYC & audit. From idea to ICO launch." />
    <link rel="canonical" href="https://onlineico.com/token-studio" />

    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="OnlineICO" />
    <meta property="og:title" content="Token Studio | Token Creation, Smart Contracts & ICO Launch | OnlineICO" />
    <meta property="og:description" content="Professional crypto services: token creation, smart contracts, KYC & audit. From idea to ICO launch." />
    <meta property="og:url" content="https://onlineico.com/token-studio" />
    <meta property="og:image" content="https://onlineico.com/og-image.png" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@online_ico" />
    <meta name="twitter:title" content="Token Studio | Token Creation, Smart Contracts & ICO Launch | OnlineICO" />
    <meta name="twitter:description" content="Professional crypto services: token creation, smart contracts, KYC & audit. From idea to ICO launch." />
    <meta name="twitter:image" content="https://onlineico.com/og-image.png" />

    <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Token Studio — OnlineICO",
        "description": "Professional crypto services: ERC-20 / BEP-20 token creation, smart contract development, KYC & audit.",
        "url": "https://onlineico.com/token-studio",
        "provider": {
            "@type": "Organization",
            "name": "OnlineICO",
            "url": "https://onlineico.com"
        },
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Token Studio Services",
            "itemListElement": [
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Token Creation", "description": "Custom ERC-20 / BEP-20 token with tokenomics and contract verification." } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Smart Contract", "description": "Presale, vesting, staking and launchpad smart contracts." } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "KYC & Audit", "description": "Smart contract audit, security report and verified KYC badge." } }
            ]
        }
    })}</script>
</Helmet>
      <Navbar />

      {/* ── HERO ── */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '5rem 1.5rem 3rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '30px', padding: '4px 16px', fontSize: '0.78rem', color: '#C4B5FD', fontWeight: '600', marginBottom: '24px', letterSpacing: '1px', textTransform: 'uppercase' }}>
          🛠️ Professional services
        </div>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#F1F5F9', lineHeight: 1.15, marginBottom: '20px' }}>
          Token Studio —{' '}
          <span style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            From idea to launch
          </span>
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '1rem', lineHeight: '1.8', maxWidth: '600px', margin: '0 auto 36px' }}>
          Do you have a crypto project but don't know where to start? Our team guides you from token creation all the way to your ICO or IDO launch.
        </p>
        <button className="vote-btn" style={{ padding: '12px 28px', fontSize: '1rem' }}
          onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}>
          Request a free quote
        </button>
      </div>

      {/* ── SERVICES ── */}
      <div style={{ maxWidth: '1100px', margin: '0 auto 5rem', padding: '0 1.5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.4rem', color: '#F1F5F9', margin: 0 }}>Our services</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
          {SERVICES.map((s, i) => (
            <div key={i} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '24px', padding: '28px', backdropFilter: 'blur(20px)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '14px' }}>{s.emoji}</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.05rem', color: '#F1F5F9', marginBottom: '10px' }}>{s.title}</div>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: '1.7', marginBottom: '16px' }}>{s.desc}</p>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', marginBottom: '16px' }} />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {s.items.map((item, j) => (
                  <li key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '0.82rem' }}>
                    <span style={{ color: s.color, fontWeight: '700', fontSize: '0.75rem' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── PROCESSUS ── */}
      <div style={{ maxWidth: '900px', margin: '0 auto 5rem', padding: '0 1.5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.4rem', color: '#F1F5F9', margin: 0 }}>Our process</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          {PROCESS.map((p, i) => (
            <div key={i} style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', padding: '24px', textAlign: 'center', backdropFilter: 'blur(20px)' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.8rem', color: p.color, marginBottom: '10px' }}>{p.step}</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', color: '#F1F5F9', fontSize: '0.95rem', marginBottom: '8px' }}>{p.title}</div>
              <div style={{ color: '#94a3b8', fontSize: '0.8rem', lineHeight: '1.6' }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FORMULAIRE CONTACT ── */}
      <div id="contact-form" style={{ maxWidth: '640px', margin: '0 auto 6rem', padding: '0 1.5rem' }}>
        <div style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '28px', padding: '40px 32px', backdropFilter: 'blur(20px)' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.3rem', color: '#F1F5F9', marginBottom: '8px' }}>Request a quote</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '28px' }}>Response within 24h — First exchange free and with no commitment.</p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>First name *</label>
                <input name="name" value={form.name} onChange={handleChange} style={inputStyle} placeholder="John" autoComplete="name" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Email *</label>
                <input name="email" value={form.email} onChange={handleChange} style={inputStyle} placeholder="john@example.com" type="email" autoComplete="email" />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Desired service *</label>
              <select name="service" value={form.service} onChange={handleChange} style={{ width: '100%', padding: '10px 14px', background: 'rgba(10,15,30,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: form.service ? '#F1F5F9' : '#64748b', fontSize: '0.88rem', fontFamily: 'Inter, sans-serif', outline: 'none', cursor: 'pointer' }}>
                <option value="">Select...</option>
                <option value="Token creation">Token creation</option>
                <option value="Smart Contract">Smart Contract</option>
                <option value="KYC & Audit">KYC & Audit</option>
                <option value="Full pack">Full pack</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Describe your project</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={4} style={{ ...inputStyle, resize: 'vertical', WebkitBoxShadow: '0 0 0 1000px rgba(11,17,32,0.95) inset' }} placeholder="Describe your project, your target blockchain, your needs..." />
            </div>

            {status === 'error' && (
                <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '10px', padding: '12px 16px', color: '#f87171', fontSize: '0.85rem' }}>
                    ⚠ {errorMsg}
                </div>
            )}
            {status === 'success' && (
                <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '10px', padding: '12px 16px', color: '#34D399', fontSize: '0.85rem' }}>
                    ✅ Message sent! We'll get back to you within 24h.
                </div>
            )}

            <button type="submit" className="vote-btn" disabled={status === 'sending'}
                style={{ padding: '13px', fontSize: '0.95rem', width: '100%', marginTop: '6px', opacity: status === 'sending' ? 0.7 : 1, cursor: status === 'sending' ? 'not-allowed' : 'pointer' }}>
                {status === 'sending' ? 'Sending...' : '🚀 Send my request'}
            </button>
          </form>
        </div>
      </div>

    </div>
  );
}
